import React from "react";
import BaseButton from "../../../components/ui/BaseButton";
import BaseModal from "../../../components/ui/BaseModal";
import { useDispatch, useSelector } from "react-redux";
import "./style.css";
import { withdrawOffer } from "../../../services/api/offers";
import { showToast } from "../../../store/toast/toastSlice";
import { useMutation } from "@tanstack/react-query";
import { SchedulePublishedCard } from "../ScheduleCard";
import LocationCard from "../LocationCard";
import RateCard from "../RateCard";
import LocationMap from "../TabsCard/LocationMap";
import { updateTask } from "../../../services/api/task";
import { useUpdateWorkOrder } from "../../../hooks/useUpdateWorkOrder";
import { updateWorkOrderSubStatus } from "../../../services/api/workOrder";
import { useCurrentUser } from "../../../hooks/useCurrentUser";

const WOConfirmationModal = ({
  woConfirmationModalOpen,
  setWOConfirmationModalOpen,
  refetchUserAllowedAction,
  woConfirmationModalData,
  tasks,
}) => {
  const { updateWorkOrder } = useUpdateWorkOrder();
  const { userData } = useCurrentUser();
  const dispatch = useDispatch();
  const workOrderDetail = useSelector(
    (state) => state.workOrder.workOrderDetails
  );
  const {
    isPending: isWithdrawOfferLoading,
    status,
    error: isWithdrawOfferError,
    mutateAsync: withdrawOfferMutation,
  } = useMutation({
    mutationFn: withdrawOffer,
    onSuccess: (data) => {
      dispatch(
        showToast({
          message: "Offer withdrawn Successfully",
          type: "success",
        })
      );
      refetchUserAllowedAction();
    },
  });

  const {
    isPending: isUpdatingSubStatusLoading,
    error: isUpdatingSubStatusError,
    mutateAsync: updateWorkOrderSubStatusMutation,
  } = useMutation({
    mutationFn: updateWorkOrderSubStatus,
  });

  const {
    isLoading: isTaskUpdateLoading,
    error: isTaskUpdateError,
    data: updatedTaskData,
    mutateAsync: updateTaskMutation,
  } = useMutation({
    mutationFn: updateTask,
    enabled: false,
  });

  const handleReadyToGo = async () => {
    const task = tasks?.find((task) => task.taskType === "START_TIME");
    try {
      let payload = {
        id: task.completedTask?.id,
        isCompleted: true,
      };

      const updatedTask = await updateTaskMutation(payload);

      updateWorkOrder();
      refetchUserAllowedAction();
      setWOConfirmationModalOpen(false);
    } catch (error) {
      dispatch(
        showToast({
          message: error?.response?.data?.message,
          type: "error",
        })
      );
    }
  };

  const handleWithdrawButton = async () => {
    try {
      await withdrawOfferMutation(workOrderDetail?.id);
      setWOConfirmationModalOpen(false);
    } catch (error) {
      dispatch(
        showToast({
          message: isWithdrawOfferError?.response?.data?.message,
          type: "error",
        })
      );
    }
  };

  const handleSubStatusButton = async (subStatus) => {
    try {
      const payload = {
        id: workOrderDetail.id,
        subStatus: subStatus,
      };
      const workOrderSubStatusResponse = await updateWorkOrderSubStatusMutation(
        payload
      );
      refetchUserAllowedAction();
      updateWorkOrder();
      dispatch(
        showToast({
          message: workOrderSubStatusResponse?.message,
          type: "success",
        })
      );
    } catch (error) {}
  };

  const handleActionTypeButton = async () => {
    const actionType = woConfirmationModalData?.actionType?.toLowerCase();
    if (actionType === "withdraw") {
      await handleWithdrawButton();
    } else if (actionType === "ready to go") {
      await handleReadyToGo();
    } else if (actionType === "confirm") {
      await handleSubStatusButton("CONFIRMED");
    }

    setWOConfirmationModalOpen(false);
  };

  return (
    <BaseModal
      // className="top-[-160px]"
      style={
        {
          // width: "1000px",
        }
      }
      open={woConfirmationModalOpen}
      close={() => {}}
    >
      <div className="container bg-[#F6F6F6 xl:w-[1000px] lg:w-[800px] w-[400px]">
        <h6
          className="text-dark border-bottom"
          style={{ marginBottom: "24px", paddingBottom: "4px" }}
        >
          {woConfirmationModalData.title}
        </h6>

        <div className="grid grid-cols-3 xl:gap-12 gap-4 mt-6">
          {!userData?.isCompanyMember && (
            <div className="lg:col-span-1 col-span-3">
              <RateCard />
            </div>
          )}
          <div className="lg:col-span-1 col-span-3">
            <SchedulePublishedCard
              title={
                workOrderDetail?.scheduleType === "WORK_BETWEEN"
                  ? "Work Between"
                  : "Fixed Start"
              }
              scheduleType={workOrderDetail?.scheduleType}
              startDate={workOrderDetail?.orderStartDate}
              startTime={workOrderDetail?.orderStartTime}
              timeZone={workOrderDetail?.address?.timeZone}
            />
          </div>
          <div className="lg:col-span-1 col-span-3">
            <LocationCard location={workOrderDetail?.address} />
          </div>
        </div>

        <div className="mt-6">
          <LocationMap location={workOrderDetail?.address} />
        </div>
      </div>
      <div className="xl:flex grid grid-cols-2 gap-4 xl:justify-center  bg-white py-6 px-4">
        <div className="xl:block col-span-2">
          <BaseButton
            className="py-[6px] px-[40px] bg-light text-dark"
            fullWidth={true}
            onClick={() => setWOConfirmationModalOpen(false)}
          >
            Cancel
          </BaseButton>
        </div>
        <div className="xl:block col-span-2">
          <BaseButton
            loaderSize={24}
            loaderColor="primary"
            isLoading={
              isUpdatingSubStatusLoading ||
              isWithdrawOfferLoading ||
              isTaskUpdateLoading
            }
            disabled={
              isUpdatingSubStatusLoading ||
              isWithdrawOfferLoading ||
              isTaskUpdateLoading
            }
            fullWidth={true}
            className="py-[6px] px-[40px]"
            color="primary"
            onClick={handleActionTypeButton}
          >
            {woConfirmationModalData.actionType}
          </BaseButton>
        </div>
      </div>
    </BaseModal>
  );
};

export default WOConfirmationModal;
